import { Stack } from '@mui/material';
import { AccessRequestClientModel, useApproveAccessRequest, useRejectAccessRequest } from '@apono/client-app-sdk';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { RequestActionModal } from '@MyTasks/components/RequestActionModal';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';
import { useEffect } from 'react';
import { toast } from 'react-toastify';

export interface RequestApprovalProps {
  request: AccessRequestClientModel;
  onApproval?: () => void;
  center?: boolean;
}

export default function RequestApproval({ request, onApproval, center }: RequestApprovalProps) {
  return (
    <Stack alignItems={center ? 'center' : 'flex-end'}>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <RejectAction request={request} onApproval={onApproval} />
        <ApproveAction request={request} onApproval={onApproval} />
      </Stack>
    </Stack>
  );
}

function RejectAction({ request, onApproval }: RequestApprovalProps) {
  const { rejectAccessRequest, isLoading, isSuccess } = useRejectAccessRequest(request.id);
  const { isEnabled: isApproverJustificationEnabled } = useFlagFixed(Flags.APPROVER_JUSTIFICATION);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Request ${request.id} Rejected`);
      onApproval?.();
    }
  }, [isSuccess, onApproval, request.id]);

  return (
    <RequestActionModal
      actionHandler={rejectAccessRequest}
      isLoading={isLoading}
      buttonLabel="Reject"
      popoverTitle={
        isApproverJustificationEnabled
          ? 'Please provide a reason for rejection'
          : `Are you sure you want to reject request ${request.id}?`
      }
      analyticsEvent={AnalyticsEvents.TaskRejectClicked}
      isApproverJustificationEnabled={isApproverJustificationEnabled}
      approverJustificationRequired={!!request.require_approver_reason}
      displayBanner={true}
    />
  );
}

function ApproveAction({ request, onApproval }: RequestApprovalProps) {
  const { approveAccessRequest, isLoading, isSuccess } = useApproveAccessRequest(request.id);
  const { isEnabled: isApproverJustificationEnabled } = useFlagFixed(Flags.APPROVER_JUSTIFICATION);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Request ${request.id} Approved`);
      onApproval?.();
    }
  }, [isSuccess, onApproval, request.id]);

  return (
    <RequestActionModal
      approverJustificationRequired={!!request.require_approver_reason}
      actionHandler={approveAccessRequest}
      isLoading={isLoading}
      buttonLabel="Approve"
      popoverTitle={
        isApproverJustificationEnabled
          ? 'Please provide a reason for approval'
          : `Are you sure you want to approve request ${request.id}`
      }
      analyticsEvent={AnalyticsEvents.TaskApproveClicked}
      isApproverJustificationEnabled={isApproverJustificationEnabled}
    />
  );
}
